import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ShoppingCartIcon,
  UserIcon,
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  CubeIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  HeartIcon,
  CreditCardIcon,
  TagIcon,
  Squares2X2Icon,
  UserGroupIcon,
  FireIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import apiService from '../../services/api';
     import AnnouncementCountdown from './AnnouncementCountdown';

const GUEST_CART_KEY = 'guestCart';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [user, setUser] = useState<any>(null);
  const [announcements, setAnnouncements] = useState<any[]>([]);

  const token = localStorage.getItem('token');

  const fetchActiveAnnouncements = useCallback(async () => {
    try {
      const response = await apiService.getActiveAnnouncements();
      if (response?.success && response?.data) setAnnouncements(response.data);
    } catch (error) {
      console.error('Navbar - Error fetching announcements:', error);
    }
  }, []);

  // a countdown reached zero: drop that announcement right away and re-sync with the server
  const handleAnnouncementExpired = useCallback(() => {
    setAnnouncements(prev => prev.filter(a => !a.timer_ends_at || new Date(a.timer_ends_at).getTime() > Date.now()));
    fetchActiveAnnouncements();
  }, [fetchActiveAnnouncements]);

  const getGuestCartCount = (): number => {
    try {
      const guestCart = localStorage.getItem(GUEST_CART_KEY);
      if (guestCart) {
        const cart = JSON.parse(guestCart);
        return cart.items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0;
      }
    } catch (e) {}
    return 0;
  };

  useEffect(() => {
    try {
      const userString = localStorage.getItem('user');
      if (userString) setUser(JSON.parse(userString));
      else setUser(null);
    } catch (error) {
      setUser(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchActiveAnnouncements();
    const interval = setInterval(fetchActiveAnnouncements, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProfileMenuOpen(false);
    setIsSearchOpen(false);
    setSearchQuery('');
  }, [location.pathname]);

  useEffect(() => {
    const fetchCartCount = () => {
      const tokenExists = localStorage.getItem('token');
      if (!tokenExists) {
        setCartCount(getGuestCartCount());
        return;
      }
      const userString = localStorage.getItem('user');
      let isCustomerUser = false;
      try {
        if (userString) {
          const parsedUser = JSON.parse(userString);
          isCustomerUser = parsedUser?.role === 'customer';
        }
      } catch (e) {}
      if (!isCustomerUser) return;
      apiService.getCart().then((response: any) => {
        if (response.success && response.data) {
          const totalItems = response.data.items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0;
          setCartCount(totalItems);
        }
      }).catch(() => {});
    };
    fetchCartCount();
    const interval = setInterval(fetchCartCount, 5000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const handleCartUpdate = () => {
      const tokenExists = localStorage.getItem('token');
      if (!tokenExists) {
        setCartCount(getGuestCartCount());
        return;
      }
      const userString = localStorage.getItem('user');
      let isCustomerUser = false;
      try {
        if (userString) {
          const parsedUser = JSON.parse(userString);
          isCustomerUser = parsedUser?.role === 'customer';
        }
      } catch (e) {}
      if (!isCustomerUser) return;
      apiService.getCart().then((response: any) => {
        if (response.success && response.data) {
          const totalItems = response.data.items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0;
          setCartCount(totalItems);
        }
      }).catch(() => {});
    };
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, [token]);

  useEffect(() => {
    const fetchWishlistCount = async () => {
      if (!token) return;
      const userString = localStorage.getItem('user');
      let isCustomerUser = false;
      try {
        if (userString) {
          const parsedUser = JSON.parse(userString);
          isCustomerUser = parsedUser?.role === 'customer';
        }
      } catch (e) {}
      if (!isCustomerUser) return;
      try {
        const response = await apiService.getWishlist();
        if (response.success && response.data) setWishlistCount(response.data.length);
      } catch (error) {}
    };
    const fetchWalletBalance = async () => {
      if (!token) return;
      const userString = localStorage.getItem('user');
      let isCustomerUser = false;
      try {
        if (userString) {
          const parsedUser = JSON.parse(userString);
          isCustomerUser = parsedUser?.role === 'customer';
        }
      } catch (e) {}
      if (!isCustomerUser) return;
      try {
        const response = await apiService.getWalletBalance();
        if (response?.success && typeof response.balance === 'number') setWalletBalance(response.balance);
        else setWalletBalance(null);
      } catch (error) {
        setWalletBalance(null);
      }
    };
    fetchWishlistCount();
    fetchWalletBalance();
    const interval = setInterval(() => {
      fetchWishlistCount();
      fetchWalletBalance();
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const isCustomer = user?.role === 'customer';
  const isEmployee = user?.role === 'employee';

  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    if (user.name) return user.name;
    if (user.username) return user.username;
    if (user.email) return user.email.split('@')[0];
    if (user.role) return `${user.role.charAt(0).toUpperCase() + user.role.slice(1)} User`;
    return 'User';
  };

  const getUserFirstName = () => {
    const fullName = getUserDisplayName();
    if (fullName.includes('@')) return fullName;
    return fullName.split(' ')[0];
  };

  const getUserRoleDisplay = () => {
    if (!user || !user.role) return 'Guest';
    switch (user.role) {
      case 'admin': return 'Administrator';
      case 'employee': return 'Employee';
      case 'customer': return 'Customer';
      default: return user.role;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setWishlistCount(0);
    setCartCount(0);
    setWalletBalance(null);
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setIsMobileMenuOpen(false);
      setSearchQuery('');
    }
  };

  const toggleSearch = () => {
    setIsSearchOpen((prev) => {
      const next = !prev;
      if (!next) setSearchQuery('');
      return next;
    });
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // ✅ Desktop nav links
  const navLinks = [
    { path: '/categories', label: 'Category', icon: Squares2X2Icon },
    { path: '/shop-by-age', label: 'Shop by Age', icon: UserGroupIcon },
    { path: '/trending', label: 'Trending', icon: FireIcon },
    { path: '/festive', label: 'Festive', icon: GiftIcon },
    ...(token && isCustomer ? [{ path: '/orders', label: 'Orders', icon: DocumentTextIcon }] : []),
    ...(token && isEmployee ? [{ path: '/employee', label: 'Dashboard', icon: DocumentTextIcon }] : []),
  ];

  // ✅ Mobile quick icons
  const mobileQuickLinks = [
    { path: '/categories', label: 'Category', icon: Squares2X2Icon },
    { path: '/shop-by-age', label: 'Shop by Age', icon: UserGroupIcon },
    { path: '/trending', label: 'Trending', icon: FireIcon },
    { path: '/festive', label: 'Festive', icon: GiftIcon },
  ];

  if (location.pathname.startsWith('/admin')) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

        .twf-nav {
          position: sticky;
          top: 0;
          z-index: 50;
          font-family: 'Plus Jakarta Sans', sans-serif;
          transition: all 0.4s ease;
        }

        .twf-ticker {
          background: linear-gradient(90deg, #FF69B4, #FF1493, #4B0082, #FF1493, #FF69B4);
          background-size: 300% 100%;
          animation: tickerGrad 6s ease infinite;
          padding: 6px 0;
          overflow: hidden;
          position: relative;
        }
        @keyframes tickerGrad {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .twf-ticker-wrapper { overflow: hidden; white-space: nowrap; position: relative; }
        .twf-ticker-inner {
          display: inline-flex;
          align-items: center;
          gap: 48px;
          animation: tickerScroll 25s linear infinite;
          white-space: nowrap;
        }
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .twf-ticker-wrapper:hover .twf-ticker-inner { animation-play-state: paused; }
        .twf-ticker-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: white;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        .twf-ticker-item span:first-child { font-size: 12px; }
        .twf-ticker-dot {
          width: 3px; height: 3px;
          background: rgba(255,255,255,0.6);
          border-radius: 50%;
          display: inline-block;
          margin: 0 4px;
        }

        .twf-bar {
          background: white;
          border-bottom: 2px solid #FFB6C1;
          transition: box-shadow 0.3s ease, background 0.3s ease;
        }
        .twf-bar.scrolled {
          background: rgba(255,255,255,0.97);
          backdrop-filter: blur(16px);
          box-shadow: 0 4px 32px rgba(255,105,180,0.14), 0 1px 6px rgba(75,0,130,0.06);
          border-bottom-color: transparent;
        }

        .twf-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 clamp(12px, 3vw, 40px);
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: 20px;
          height: 64px;
        }

        .twf-logo {
          display: flex;
          align-items: center;
          text-decoration: none;
          flex-shrink: 0;
        }
        .twf-logo img {
          height: 42px;
          width: auto;
          object-fit: contain;
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), filter 0.3s ease;
          filter: drop-shadow(0 3px 6px rgba(255,105,180,0.2));
        }
        .twf-logo:hover img {
          transform: scale(1.06);
          filter: drop-shadow(0 6px 14px rgba(255,20,147,0.35));
        }
        .twf-logo-fallback {
          width: 40px; height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, #FF69B4, #FF1493, #4B0082);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .twf-logo-fallback svg { width: 20px; height: 20px; color: white; }
        .twf-logo:hover .twf-logo-fallback { transform: rotate(8deg) scale(1.08); }
        .twf-logo-tm {
          align-self: flex-start;
          margin: 3px 0 0 2px;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
          color: #4B0082;
          opacity: 0.85;
          letter-spacing: 0;
          user-select: none;
        }

        /* ===== LEFT LINKS (desktop) ===== */
        .twf-left-links {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 2px;
          flex-wrap: nowrap;
        }
        .twf-link {
          position: relative;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 6px 10px;
          border-radius: 10px;
          font-size: 12.5px;
          font-weight: 600;
          color: #555;
          text-decoration: none;
          transition: color 0.2s, background 0.2s;
          overflow: hidden;
          white-space: nowrap;
        }
        .twf-link::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%) scaleX(0);
          width: calc(100% - 16px);
          height: 2px;
          border-radius: 99px;
          background: linear-gradient(90deg, #FF69B4, #4B0082);
          transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
        }
        .twf-link:hover { color: #FF1493; background: rgba(255,182,193,0.18); }
        .twf-link:hover::after { transform: translateX(-50%) scaleX(1); }
        .twf-link.active {
          color: #4B0082;
          background: linear-gradient(135deg, rgba(255,182,193,0.25), rgba(248,240,255,0.5));
        }
        .twf-link.active::after { transform: translateX(-50%) scaleX(1); }
        .twf-link svg {
          width: 14px; height: 14px;
          transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
        }
        .twf-link:hover svg { transform: translateY(-2px) scale(1.15); }

        .twf-link.trending-link { color: #FF4500; }
        .twf-link.trending-link:hover { color: #FF1493; background: rgba(255,182,193,0.18); }
        .twf-link.festive-link { color: #C5A059; }
        .twf-link.festive-link:hover { color: #C27B7F; background: rgba(255,182,193,0.18); }

        /* ===== MOBILE QUICK ICONS ===== */
        .twf-mobile-quick {
          display: none;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
        }
        .twf-quick-icon {
          position: relative;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #4B0082;
          text-decoration: none;
          background: linear-gradient(135deg, #FFF0F5, #F8F0FF);
          border: 1.5px solid transparent;
          transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.22s cubic-bezier(0.34,1.56,0.64,1);
        }
        .twf-quick-icon:hover,
        .twf-quick-icon.active {
          background: rgba(255,182,193,0.35);
          border-color: #FF69B4;
          color: #FF1493;
          transform: scale(1.06);
        }
        .twf-quick-icon svg { width: 19px; height: 19px; }
        .twf-quick-icon.trending-quick { color: #FF4500; }
        .twf-quick-icon.trending-quick:hover,
        .twf-quick-icon.trending-quick.active { color: #FF1493; }
        .twf-quick-icon.festive-quick { color: #C5A059; }
        .twf-quick-icon.festive-quick:hover,
        .twf-quick-icon.festive-quick.active { color: #C27B7F; }

        .twf-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }

        /* ===== EXPANDABLE SEARCH ===== */
        .twf-search-container {
          display: flex;
          align-items: center;
          gap: 0;
          position: relative;
        }
        .twf-search-form {
          display: flex;
          align-items: center;
          overflow: hidden;
          max-width: 0;
          opacity: 0;
          transition: max-width 0.35s cubic-bezier(0.34,1.56,0.64,1), opacity 0.25s ease, margin 0.3s ease;
          margin-right: 0;
        }
        .twf-search-form.open {
          max-width: 260px;
          opacity: 1;
          margin-right: 6px;
        }
        .twf-search-input {
          width: 240px;
          padding: 9px 12px 9px 34px;
          border: 1.5px solid #FFB6C1;
          border-radius: 10px;
          font-size: 13px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #FFF5F8;
          color: #333;
          outline: none;
          transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
        }
        .twf-search-input:focus {
          border-color: #FF69B4;
          background: white;
          box-shadow: 0 0 0 3px rgba(255,105,180,0.14);
        }
        .twf-search-input::placeholder { color: #cca; }
        .twf-search-icon-inside {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          width: 13px; height: 13px;
          color: #FF69B4;
          pointer-events: none;
          z-index: 1;
        }

        .twf-icon-btn {
          position: relative;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid transparent;
          background: linear-gradient(135deg, #FFF0F5, #F8F0FF);
          color: #777;
          cursor: pointer;
          text-decoration: none;
          transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.22s cubic-bezier(0.34,1.56,0.64,1);
        }
        .twf-icon-btn:hover {
          background: rgba(255,182,193,0.35);
          border-color: #FF69B4;
          color: #FF1493;
          transform: scale(1.1);
        }
        .twf-icon-btn svg { width: 24px; height: 24px; }
        .twf-icon-btn .twf-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          min-width: 20px;
          height: 20px;
          background: linear-gradient(135deg, #FF69B4, #4B0082);
          color: white;
          font-size: 10px;
          font-weight: 700;
          border-radius: 99px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
          border: 2px solid white;
          box-shadow: 0 2px 10px rgba(255,105,180,0.45);
        }
        .twf-icon-btn.active-search {
          background: linear-gradient(135deg, #FF69B4, #FF1493);
          color: white;
          border-color: #FF1493;
        }
        .twf-icon-btn.active-search:hover {
          background: linear-gradient(135deg, #FF1493, #4B0082);
          color: white;
        }

        .twf-user-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 12px 4px 4px;
          border-radius: 999px;
          border: 1.5px solid #FFB6C1;
          background: linear-gradient(135deg, #FFF0F5, #F8F0FF);
          cursor: pointer;
          transition: all 0.25s ease;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .twf-user-pill:hover {
          border-color: #FF69B4;
          box-shadow: 0 4px 18px rgba(255,105,180,0.22);
          transform: translateY(-1px);
        }
        .twf-user-pill.open {
          border-color: #FF1493;
          box-shadow: 0 4px 18px rgba(255,105,180,0.3);
        }
        .twf-avatar {
          width: 28px; height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FF69B4, #FF1493, #4B0082);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: relative;
        }
        .twf-avatar svg { width: 14px; height: 14px; color: white; }
        .twf-avatar-dot {
          position: absolute;
          bottom: -1px; right: -1px;
          width: 8px; height: 8px;
          background: #22c55e;
          border-radius: 50%;
          border: 2px solid white;
        }
        .twf-user-name {
          font-size: 12.5px;
          font-weight: 600;
          color: #4B0082;
          max-width: 80px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .twf-chevron {
          width: 13px; height: 13px;
          color: #FF69B4;
          transition: transform 0.25s ease;
          flex-shrink: 0;
        }
        .twf-user-pill.open .twf-chevron { transform: rotate(180deg); }

        .twf-btn-ghost {
          padding: 7px 14px;
          border-radius: 10px;
          border: none;
          background: transparent;
          font-size: 13px;
          font-weight: 600;
          color: #777;
          cursor: pointer;
          text-decoration: none;
          transition: background 0.2s, color 0.2s;
          font-family: 'Plus Jakarta Sans', sans-serif;
          white-space: nowrap;
        }
        .twf-btn-ghost:hover { background: rgba(255,182,193,0.25); color: #FF1493; }

        .twf-btn-solid {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          background: linear-gradient(135deg, #FF69B4, #FF1493, #4B0082);
          color: white;
          font-size: 13px;
          font-weight: 700;
          text-decoration: none;
          border: none;
          cursor: pointer;
          transition: all 0.26s cubic-bezier(0.34,1.56,0.64,1);
          font-family: 'Plus Jakarta Sans', sans-serif;
          white-space: nowrap;
          position: relative;
          overflow: hidden;
        }
        .twf-btn-solid::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #FF1493, #FF69B4, #663399);
          opacity: 0;
          transition: opacity 0.25s;
        }
        .twf-btn-solid:hover::before { opacity: 1; }
        .twf-btn-solid:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(255,20,147,0.4);
        }
        .twf-btn-solid span { position: relative; z-index: 1; }
        .twf-btn-solid svg {
          width: 13px; height: 13px;
          position: relative; z-index: 1;
          transition: transform 0.2s;
        }
        .twf-btn-solid:hover svg { transform: translateX(3px); }

        .twf-dropdown {
          position: absolute;
          right: 0;
          top: calc(100% + 10px);
          width: 280px;
          background: white;
          border-radius: 18px;
          border: 1.5px solid #FFB6C1;
          box-shadow: 0 20px 60px rgba(255,105,180,0.18), 0 4px 16px rgba(75,0,130,0.08);
          overflow: hidden;
          z-index: 60;
          animation: dropIn 0.25s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-12px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .twf-dropdown-header {
          padding: 14px 16px;
          background: linear-gradient(135deg, #FFF0F5, #F8F0FF);
          border-bottom: 1.5px solid #FFB6C1;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .twf-dropdown-avatar {
          width: 38px; height: 38px;
          border-radius: 12px;
          background: linear-gradient(135deg, #FF69B4, #4B0082);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .twf-dropdown-avatar svg { width: 18px; height: 18px; color: white; }
        .twf-dropdown-name {
          font-size: 13px;
          font-weight: 700;
          color: #111;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 2px;
        }
        .twf-dropdown-email {
          font-size: 11px;
          color: #999;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .twf-dropdown-role {
          display: inline-block;
          margin-top: 4px;
          padding: 2px 8px;
          border-radius: 99px;
          font-size: 9px;
          font-weight: 700;
          background: linear-gradient(135deg, rgba(255,182,193,0.5), rgba(248,240,255,0.8));
          color: #4B0082;
          border: 1px solid rgba(255,105,180,0.3);
        }
        .twf-dropdown-body { padding: 6px; }
        .twf-dropdown-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          border-radius: 10px;
          font-size: 12.5px;
          font-weight: 500;
          color: #444;
          text-decoration: none;
          transition: background 0.18s, color 0.18s;
          cursor: pointer;
          border: none;
          background: transparent;
          width: 100%;
          text-align: left;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .twf-dropdown-item:hover { background: rgba(255,182,193,0.22); color: #FF1493; }
        .twf-dropdown-item svg { width: 15px; height: 15px; flex-shrink: 0; }
        .twf-dropdown-item.offers-item {
          color: #FF1493;
          background: linear-gradient(135deg, rgba(255,182,193,0.15), rgba(255,240,248,0.3));
        }
        .twf-dropdown-item.offers-item:hover { background: rgba(255,105,180,0.18); color: #c9006e; }
        .twf-dropdown-item-badge {
          margin-left: auto;
          padding: 1px 6px;
          background: linear-gradient(135deg, #FF69B4, #FF1493);
          color: white;
          font-size: 8px;
          font-weight: 800;
          border-radius: 99px;
          letter-spacing: 0.3px;
        }
        .twf-dropdown-item.logout { color: #FF1493; }
        .twf-dropdown-item.logout:hover { background: rgba(255,105,180,0.12); color: #FF1493; }
        .twf-dropdown-divider { height: 1px; background: #FFB6C1; margin: 4px 6px; }
        .twf-dropdown-count {
          margin-left: auto;
          background: #FF69B4;
          color: white;
          font-size: 9px;
          font-weight: 700;
          border-radius: 99px;
          padding: 1px 6px;
          min-width: 40px;
          text-align: center;
        }

        .twf-hamburger {
          display: none;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          border: 1.5px solid #FFB6C1;
          background: linear-gradient(135deg, #FFF0F5, #F8F0FF);
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.22s;
          flex-shrink: 0;
        }
        .twf-hamburger:hover { background: rgba(255,182,193,0.4); border-color: #FF69B4; }
        .twf-hamburger svg { width: 17px; height: 17px; color: #4B0082; transition: transform 0.3s; }
        .twf-hamburger.open svg { transform: rotate(90deg); }

        .twf-drawer {
          border-top: 1.5px solid #FFB6C1;
          background: linear-gradient(180deg, #FFF0F5, #F8F0FF);
          padding: 10px;
          animation: slideDown 0.25s ease;
          max-height: calc(100vh - 100px);
          overflow-y: auto;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .twf-drawer-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 12px;
          font-size: 13.5px;
          font-weight: 600;
          color: #555;
          text-decoration: none;
          transition: all 0.2s;
          margin-bottom: 2px;
        }
        .twf-drawer-link:hover { background: rgba(255,255,255,0.7); color: #FF1493; }
        .twf-drawer-link.active {
          background: white;
          color: #4B0082;
          box-shadow: 0 2px 12px rgba(255,105,180,0.15);
          border-left: 3px solid #FF69B4;
        }
        .twf-drawer-link.offers-drawer-link {
          color: #FF1493;
          background: linear-gradient(135deg, rgba(255,182,193,0.2), rgba(255,240,248,0.4));
        }
        .twf-drawer-link.offers-drawer-link:hover { background: rgba(255,105,180,0.2); }
        .twf-drawer-link svg { width: 17px; height: 17px; flex-shrink: 0; }
        .twf-drawer-link .twf-drawer-count {
          margin-left: auto;
          background: #FF69B4;
          color: white;
          font-size: 10px;
          font-weight: 700;
          border-radius: 99px;
          padding: 1px 6px;
          min-width: 40px;
          text-align: center;
        }
        .twf-drawer-offers-badge {
          margin-left: auto;
          padding: 1px 6px;
          background: linear-gradient(135deg, #FF69B4, #FF1493);
          color: white;
          font-size: 8px;
          font-weight: 800;
          border-radius: 99px;
        }
        .twf-drawer-auth {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1.5px solid #FFB6C1;
        }
        .twf-drawer-user {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: rgba(255,255,255,0.7);
          border-radius: 12px;
          margin-bottom: 6px;
        }
        .twf-drawer-user-avatar {
          width: 36px; height: 36px;
          border-radius: 10px;
          background: linear-gradient(135deg, #FF69B4, #4B0082);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .twf-drawer-user-avatar svg { width: 16px; height: 16px; color: white; }
        .twf-drawer-user-info { flex: 1; min-width: 0; }
        .twf-drawer-user-name {
          font-size: 13px;
          font-weight: 700;
          color: #111;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .twf-drawer-user-email {
          font-size: 10.5px;
          color: #999;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .twf-drawer-user-role {
          display: inline-block;
          margin-top: 3px;
          padding: 1px 6px;
          border-radius: 99px;
          font-size: 8px;
          font-weight: 700;
          background: linear-gradient(135deg, rgba(255,182,193,0.5), rgba(248,240,255,0.8));
          color: #4B0082;
          border: 1px solid rgba(255,105,180,0.3);
        }

        /* ===================================================== */
        /* =============== MOBILE RESPONSIVE =================== */
        /* ===================================================== */
        @media (max-width: 767px) {
          .twf-desktop-only,
          .twf-desktop-auth,
          .twf-left-links { display: none !important; }

          .twf-inner {
            height: 54px;
            padding: 0 8px;
            gap: 6px;
            display: flex !important;
            align-items: center;
          }

          .twf-mobile-quick {
            display: flex !important;
            margin-left: 4px;
          }

          .twf-actions {
            margin-left: auto;
            gap: 5px;
          }

          .twf-ticker { padding: 4px 0; }
          .twf-ticker-item { font-size: 9px; }
          .twf-ticker-item span:first-child { font-size: 10px; }
          .twf-ticker-dot { width: 2px; height: 2px; margin: 0 3px; }

          .twf-logo img { height: 32px; }
          .twf-logo-tm { font-size: 9px; margin-top: 2px; }
          .twf-logo-fallback { width: 32px; height: 32px; }
          .twf-logo-fallback svg { width: 16px; height: 16px; }

          .twf-quick-icon {
            width: 34px;
            height: 34px;
            border-radius: 9px;
          }
          .twf-quick-icon svg { width: 18px; height: 18px; }

          .twf-icon-btn { width: 38px; height: 38px; border-radius: 10px; }
          .twf-icon-btn svg { width: 20px; height: 20px; }
          .twf-icon-btn .twf-badge {
            min-width: 17px;
            height: 17px;
            font-size: 8px;
            top: -3px;
            right: -3px;
            padding: 0 3px;
          }

          .twf-search-form.open { max-width: 170px; }
          .twf-search-input { width: 160px; padding: 7px 10px 7px 30px; font-size: 12px; }
          .twf-search-icon-inside { width: 12px; height: 12px; left: 8px; }

          .twf-hamburger { display: flex; width: 38px; height: 38px; }

          .twf-user-name { display: none; }
          .twf-user-pill { padding: 3px 8px 3px 3px; gap: 4px; }
          .twf-chevron { display: none; }
          .twf-avatar { width: 24px; height: 24px; }
          .twf-avatar svg { width: 12px; height: 12px; }
          .twf-avatar-dot { width: 6px; height: 6px; }

          .twf-drawer { padding: 8px; }
          .twf-drawer-link { padding: 8px 10px; font-size: 12.5px; }
          .twf-drawer-link svg { width: 15px; height: 15px; }
          .twf-drawer-user { padding: 8px 10px; }
          .twf-drawer-user-avatar { width: 32px; height: 32px; }
          .twf-drawer-user-avatar svg { width: 14px; height: 14px; }
          .twf-drawer-user-name { font-size: 12px; }
          .twf-drawer-user-email { font-size: 10px; }

          .twf-btn-ghost { padding: 5px 10px; font-size: 12px; }
          .twf-btn-solid { padding: 6px 12px; font-size: 12px; }
          .twf-btn-solid svg { width: 12px; height: 12px; }
        }

        @media (max-width: 380px) {
          .twf-inner { height: 48px; padding: 0 6px; gap: 4px; }
          .twf-logo img { height: 28px; }
          .twf-logo-tm { font-size: 8px; margin-top: 1px; }
          .twf-logo-fallback { width: 28px; height: 28px; }
          .twf-logo-fallback svg { width: 14px; height: 14px; }
          .twf-quick-icon { width: 30px; height: 30px; border-radius: 8px; }
          .twf-quick-icon svg { width: 16px; height: 16px; }
          .twf-icon-btn { width: 34px; height: 34px; }
          .twf-icon-btn svg { width: 18px; height: 18px; }
          .twf-hamburger { width: 34px; height: 34px; }
          .twf-hamburger svg { width: 15px; height: 15px; }
          .twf-user-pill { padding: 2px 6px 2px 2px; gap: 3px; }
          .twf-avatar { width: 22px; height: 22px; }
          .twf-avatar svg { width: 11px; height: 11px; }
          .twf-avatar-dot { width: 5px; height: 5px; }
          .twf-actions { gap: 3px; }
          .twf-mobile-quick { gap: 1px; margin-left: 2px; }
          .twf-ticker-item { font-size: 8px; }
          .twf-ticker-item span:first-child { font-size: 9px; }
          .twf-ticker { padding: 3px 0; }
          .twf-icon-btn .twf-badge {
            min-width: 15px;
            height: 15px;
            font-size: 7px;
            top: -2px;
            right: -2px;
          }
          .twf-dropdown { width: 250px; right: -10px; }
          .twf-search-form.open { max-width: 120px; }
          .twf-search-input { width: 110px; padding: 6px 8px 6px 26px; font-size: 11px; }
        }

        @media (min-width: 768px) {
          .twf-hamburger { display: none; }
          .twf-mobile-quick { display: none !important; }
          .twf-mobile-only { display: none !important; }
        }

        @media (max-width: 1023px) and (min-width: 768px) {
          .twf-btn-ghost { padding: 6px 10px; font-size: 12px; }
          .twf-btn-solid { padding: 6px 12px; font-size: 12px; }
          .twf-actions { gap: 8px; }
          .twf-inner { gap: 12px; height: 58px; }
          .twf-icon-btn { width: 40px; height: 40px; }
          .twf-icon-btn svg { width: 22px; height: 22px; }
          .twf-logo img { height: 36px; }
          .twf-link { padding: 5px 8px; font-size: 11px; }
          .twf-link svg { width: 12px; height: 12px; }
          .twf-search-form.open { max-width: 200px; }
          .twf-search-input { width: 190px; }
        }
      `}</style>

      <nav className="twf-nav">
        {announcements.length > 0 && (
          <div className="twf-ticker">
            <div className="twf-ticker-wrapper">
              <div className="twf-ticker-inner">
                {[...Array(2)].map((_, gi) => (
                  <React.Fragment key={gi}>
                    {announcements.map((ann, idx) => (
                      <React.Fragment key={`${gi}-${ann.id || idx}`}>
                        <span className="twf-ticker-item">
                          <span>{ann.emoji || '✨'}</span>
                          <span>{ann.text}</span>
                          {ann.timer_ends_at && (
                            <AnnouncementCountdown endsAt={ann.timer_ends_at} onExpire={handleAnnouncementExpired} />
                          )}
                        </span>
                        <span className="twf-ticker-dot" />
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={`twf-bar ${isScrolled ? 'scrolled' : ''}`}>
          <div className="twf-inner">
            {/* LOGO */}
            <Link to="/" className="twf-logo">
              {!logoError ? (
                <img src="/logo.png" alt="Twinklefanta" onError={() => setLogoError(true)} />
              ) : (
                <div className="twf-logo-fallback">
                  <SparklesIcon />
                </div>
              )}
              <span className="twf-logo-tm" aria-hidden="true">™</span>
            </Link>

            {/* MOBILE QUICK ICONS */}
            <div className="twf-mobile-quick">
              {mobileQuickLinks.map((link) => {
                const Icon = link.icon;
                const isTrending = link.label === 'Trending';
                const isFestive = link.label === 'Festive';
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`twf-quick-icon ${isActive(link.path) ? 'active' : ''} ${
                      isTrending ? 'trending-quick' : isFestive ? 'festive-quick' : ''
                    }`}
                    aria-label={link.label}
                    title={link.label}
                  >
                    <Icon />
                  </Link>
                );
              })}
            </div>

            {/* DESKTOP LINKS */}
            <div className="twf-left-links">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const extraClass = link.label === 'Trending' ? 'trending-link' : link.label === 'Festive' ? 'festive-link' : '';
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`twf-link ${isActive(link.path) ? 'active' : ''} ${extraClass}`}
                  >
                    <Icon />
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* ACTIONS */}
            <div className="twf-actions">
              <div className="twf-search-container">
                <form
                  onSubmit={handleSearch}
                  className={`twf-search-form ${isSearchOpen ? 'open' : ''}`}
                >
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <MagnifyingGlassIcon className="twf-search-icon-inside" />
                    <input
                      type="text"
                      className="twf-search-input"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus={isSearchOpen}
                    />
                  </div>
                </form>

                <button
                  type="button"
                  className={`twf-icon-btn ${isSearchOpen ? 'active-search' : ''}`}
                  onClick={toggleSearch}
                  aria-label={isSearchOpen ? 'Close search' : 'Open search'}
                >
                  {isSearchOpen ? (
                    <XMarkIcon style={{ width: '24px', height: '24px' }} />
                  ) : (
                    <MagnifyingGlassIcon style={{ width: '24px', height: '24px' }} />
                  )}
                </button>
              </div>

              {token && isCustomer && (
                <Link to="/wishlist" className="twf-icon-btn twf-desktop-only" aria-label="Wishlist">
                  {wishlistCount > 0 ? (
                    <HeartIconSolid style={{ width: '24px', height: '24px', color: '#FF1493' }} />
                  ) : (
                    <HeartIcon style={{ width: '24px', height: '24px' }} />
                  )}
                  {wishlistCount > 0 && (
                    <span className="twf-badge">{wishlistCount > 99 ? '99+' : wishlistCount}</span>
                  )}
                </Link>
              )}

              <Link to="/cart" className="twf-icon-btn" aria-label="Cart">
                <ShoppingCartIcon style={{ width: '24px', height: '24px' }} />
                {cartCount > 0 && (
                  <span className="twf-badge">{cartCount > 99 ? '99+' : cartCount}</span>
                )}
              </Link>

              {token && isCustomer && (
                <Link to="/wallet" className="twf-icon-btn twf-desktop-only" aria-label="Wallet">
                  <CreditCardIcon style={{ width: '24px', height: '24px' }} />
                  {walletBalance !== null && walletBalance > 0 && (
                    <span className="twf-badge" style={{ fontSize: '8px', minWidth: '24px' }}>
                      ₹{walletBalance >= 1000
                        ? `${(walletBalance / 1000).toFixed(1)}k`
                        : walletBalance.toFixed(0)}
                    </span>
                  )}
                </Link>
              )}

              {token ? (
                <div style={{ position: 'relative' }} className="twf-desktop-only">
                  <button
                    className={`twf-user-pill ${isProfileMenuOpen ? 'open' : ''}`}
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    aria-label="Profile menu"
                  >
                    <div className="twf-avatar">
                      <UserIcon />
                      <span className="twf-avatar-dot" />
                    </div>
                    <span className="twf-user-name">{getUserFirstName()}</span>
                    <svg className="twf-chevron" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {isProfileMenuOpen && (
                    <>
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 50 }}
                        onClick={() => setIsProfileMenuOpen(false)}
                      />
                      <div className="twf-dropdown">
                        <div className="twf-dropdown-header">
                          <div className="twf-dropdown-avatar">
                            <UserIcon />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="twf-dropdown-name">{getUserDisplayName()}</div>
                            <div className="twf-dropdown-email">{user?.email}</div>
                            <span className="twf-dropdown-role">{getUserRoleDisplay()}</span>
                          </div>
                        </div>
                        <div className="twf-dropdown-body">
                          <Link
                            to="/profile"
                            className="twf-dropdown-item"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <UserIcon /> My Profile
                          </Link>
                          {isCustomer && (
                            <>
                              <Link
                                to="/orders"
                                className="twf-dropdown-item"
                                onClick={() => setIsProfileMenuOpen(false)}
                              >
                                <DocumentTextIcon /> My Orders
                              </Link>
                              <Link
                                to="/wishlist"
                                className="twf-dropdown-item"
                                onClick={() => setIsProfileMenuOpen(false)}
                              >
                                <HeartIcon /> Wishlist
                                {wishlistCount > 0 && (
                                  <span className="twf-dropdown-count">{wishlistCount}</span>
                                )}
                              </Link>
                              <Link
                                to="/wallet"
                                className="twf-dropdown-item"
                                onClick={() => setIsProfileMenuOpen(false)}
                              >
                                <CreditCardIcon /> Wallet
                                {walletBalance !== null && walletBalance > 0 && (
                                  <span className="twf-dropdown-count">
                                    ₹{walletBalance.toLocaleString('en-IN', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                )}
                              </Link>
                              <Link
                                to="/offers"
                                className="twf-dropdown-item offers-item"
                                onClick={() => setIsProfileMenuOpen(false)}
                              >
                                <TagIcon /> Offers & Deals
                                <span className="twf-dropdown-item-badge">HOT</span>
                              </Link>
                            </>
                          )}
                          {isEmployee && (
                            <Link
                              to="/employee"
                              className="twf-dropdown-item"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <DocumentTextIcon /> Employee Dashboard
                            </Link>
                          )}
                          <div className="twf-dropdown-divider" />
                          <button className="twf-dropdown-item logout" onClick={handleLogout}>
                            <ArrowRightOnRectangleIcon /> Logout
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div
                  className="twf-desktop-auth"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Link to="/login" className="twf-btn-ghost">Login</Link>
                  <Link to="/register" className="twf-btn-solid">
                    <span>Register</span>
                    <ArrowRightOnRectangleIcon />
                  </Link>
                </div>
              )}

              <button
                className={`twf-hamburger ${isMobileMenuOpen ? 'open' : ''}`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <XMarkIcon /> : <Bars3Icon />}
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="twf-drawer">
              {token && (
                <div className="twf-drawer-user">
                  <div className="twf-drawer-user-avatar">
                    <UserIcon />
                  </div>
                  <div className="twf-drawer-user-info">
                    <div className="twf-drawer-user-name">{getUserDisplayName()}</div>
                    <div className="twf-drawer-user-email">{user?.email}</div>
                    <span className="twf-drawer-user-role">{getUserRoleDisplay()}</span>
                  </div>
                </div>
              )}

              {token && (
                <Link
                  to="/profile"
                  className="twf-drawer-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <UserIcon /> My Profile
                </Link>
              )}

              <Link
                to="/products"
                className={`twf-drawer-link ${isActive('/products') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <CubeIcon /> Products
              </Link>

              {token && isCustomer && (
                <Link
                  to="/wishlist"
                  className="twf-drawer-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <HeartIcon /> Wishlist
                  {wishlistCount > 0 && <span className="twf-drawer-count">{wishlistCount}</span>}
                </Link>
              )}

              <Link
                to="/cart"
                className="twf-drawer-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <ShoppingCartIcon /> Cart
                {cartCount > 0 && <span className="twf-drawer-count">{cartCount}</span>}
              </Link>

              {token && isCustomer && (
                <>
                  <Link
                    to="/orders"
                    className={`twf-drawer-link ${isActive('/orders') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <DocumentTextIcon /> My Orders
                  </Link>
                  <Link
                    to="/wallet"
                    className="twf-drawer-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <CreditCardIcon /> Wallet
                    {walletBalance !== null && walletBalance > 0 && (
                      <span className="twf-drawer-count">
                        ₹{walletBalance.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/offers"
                    className="twf-drawer-link offers-drawer-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <TagIcon /> Offers & Deals
                    <span className="twf-drawer-offers-badge">HOT</span>
                  </Link>
                </>
              )}

              {token && isEmployee && (
                <Link
                  to="/employee"
                  className={`twf-drawer-link ${isActive('/employee') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <DocumentTextIcon /> Employee Dashboard
                </Link>
              )}

              {!token && (
                <div className="twf-drawer-auth">
                  <Link
                    to="/login"
                    className="twf-drawer-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <ArrowRightOnRectangleIcon /> Login
                  </Link>
                  <Link
                    to="/register"
                    className="twf-drawer-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <UserIcon /> Register
                  </Link>
                </div>
              )}

              {token && (
                <div className="twf-drawer-auth">
                  <button
                    className="twf-drawer-link"
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ width: '100%', color: '#FF1493' }}
                  >
                    <ArrowRightOnRectangleIcon /> Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;